import React, { useState } from "react";
import { toast } from "sonner";
import { At } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { INVITE, PasswordFallback, needsWelcome } from "@/components/user-wizard/ResendWelcome";
import { EmailTypoHint } from "@/components/user-wizard/EmailTypoHint";
import { needsTypoConfirm } from "@/lib/emailTypos";

const BTN = "border border-black px-3 py-1.5 text-xs uppercase tracking-widest transition-colors duration-100 disabled:opacity-50";

// Only accounts nobody has used yet: an unaccepted invite or a temporary password that was never used.
export const canFixEmail = (u) => u.role !== "super_admin" && (INVITE.includes(u.status) || needsWelcome(u));

// Toasts the outcome; returns the temporary password only if the welcome email couldn't be sent.
function report(data) {
  if (data.state === "invite") {
    navigator.clipboard?.writeText(data.invitation_url).catch(() => {});
    if (data.email_sent) {
      toast.success(`Email changed. A fresh invitation went to ${data.email}`);
    } else {
      toast.warning(`Email changed to ${data.email}, but the invitation couldn't be sent. The link is copied so you can share it.`);
    }
    return null;
  }
  if (data.email_sent) {
    toast.success(`Email changed. The welcome email went to ${data.email}`);
  } else {
    toast.warning(`Email changed to ${data.email}, but the welcome email couldn't be sent.`);
  }
  return data.email_sent ? null : data.temp_password;
}

function FixEmailDialog({ user, onClose, onSaved }) {
  const [email, setEmail] = useState(user.email);
  const [ok, setOk] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const invite = INVITE.includes(user.status);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/_admin/user-wizard/accounts/${user.id}/fix-email`, { email: email.trim() });
      onSaved(report(data), data.email);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Enter a valid email address");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-none border-2 border-black sm:max-w-md" data-testid="fix-email-dialog">
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle className="font-chivo text-xl font-black">Fix {user.name}&apos;s email</DialogTitle>
            <DialogDescription>
              {invite ? "We'll send a fresh invitation to the new address. The old invite link stops working."
                : "We'll send a new temporary password and the getting-started steps to the new address. The old password stops working."}
            </DialogDescription>
          </DialogHeader>
          <label className="label-mono block mt-4 mb-1" htmlFor="fix-email-input">Correct email address</label>
          <input id="fix-email-input" type="email" required autoFocus value={email} onChange={(e) => { setEmail(e.target.value); setError(""); }}
            data-testid="fix-email-input" className="border border-black px-3 py-2 w-full text-sm font-mono" />
          <div className="text-[11px] font-mono text-zinc-500 mt-1" data-testid="fix-email-current">Now: {user.email}</div>
          <EmailTypoHint email={email} confirmed={ok} testId="fix-email-typo"
            onUse={(fixed) => { setEmail(fixed); setError(""); }} onConfirm={() => setOk(email.trim())} />
          {error && <div className="text-xs text-red-700 mt-2" data-testid="fix-email-error">{error}</div>}
          <DialogFooter className="gap-2 mt-5">
            <button type="button" onClick={onClose} className={`${BTN} hover:bg-zinc-100`} data-testid="fix-email-cancel">Cancel</button>
            <button type="submit" disabled={busy || !email.trim() || needsTypoConfirm(email, ok)} className={`${BTN} bg-black text-white hover:bg-zinc-800`} data-testid="fix-email-save">
              {busy ? "Sending…" : "Save and resend"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function FixEmailButton({ user, testId, onDone, className }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState({ value: null, email: "" });
  const saved = (value, email) => {
    setOpen(false);
    setPw({ value, email });
    onDone?.();
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={testId} className={className}>
        <At size={10} className="inline" /> Fix email
      </button>
      {open && <FixEmailDialog user={user} onClose={() => setOpen(false)} onSaved={saved} />}
      <PasswordFallback pw={pw.value} email={pw.email} onClose={() => setPw({ value: null, email: "" })} />
    </>
  );
}
